export function genId() {
    return crypto.randomUUID();
}
// ── Bulk item parsing ──────────────────────────────────────────────────────
// Strips a single leading list marker from a line: dash/asterisk/bullet glyphs
// or a numbered prefix like `1.` / `1)`. Whitespace (including a leading RTL
// mark) before the marker is tolerated. Kept deliberately simple — we only peel
// the common bullet styles produced by WhatsApp, Notes, and Google Keep.
const BULLET_PREFIX = /^[\s‎‏]*(?:[-*•·‣◦‒–]|\d+[.)])\s+/u;
/**
 * Parse a pasted/typed block of text into individual item strings.
 *
 * - Each non-empty line becomes one item.
 * - Common bullet/number prefixes are stripped (`- `, `* `, `• `, `1. `, `1) `).
 * - Whitespace is trimmed; empty lines are ignored.
 * - Ordering from the source text is preserved.
 */
export function parseBulkItems(text) {
    return text
        .split(/\r?\n/)
        .map((line) => line.replace(BULLET_PREFIX, "").trim())
        .filter((line) => line.length > 0);
}
/** A category section's internal key — always derived from its stable id. */
export function sectionToTab(section) {
    return `section:${section.id}`;
}
/** A smart collection's internal key. */
export function collectionToTab(id) {
    return `collection:${id}`;
}
export const TAB_LABELS = {
    overview: "סקירה",
    mine: "שלי",
    collections: "אוספים",
    categories: "קטגוריות",
    management: "ניהול",
};
export function tabLabel(tab, sections) {
    if (TAB_LABELS[tab])
        return TAB_LABELS[tab];
    if (tab.startsWith("section:")) {
        const id = tab.slice("section:".length);
        return sections.find((s) => s.id === id)?.title ?? "עוד";
    }
    return tab;
}
// ── URL view resolution ────────────────────────────────────────────────────
/** The five fixed top-level destinations. */
export const TOP_LEVEL_TABS = [
    "overview",
    "mine",
    "collections",
    "categories",
    "management",
];
/** Stable ids of the smart collections (kept in sync with CollectionsTab). */
export const COLLECTION_IDS = ["buy_there", "missing", "open"];
/**
 * Resolve the active view from the URL's `view` param. The URL is the source of
 * truth; `fallback` (from localStorage / default) is used only when the param is
 * absent. Drilled-in views are validated against `content` once it has loaded so
 * deleted categories/collections fall back gracefully instead of rendering blank.
 *
 * - no `viewParam` → `fallback`.
 * - a top-level tab → returned as-is.
 * - `section:<id>` → kept provisionally while `content` is null (so a refresh into
 *   a drilled category isn't lost before load); once loaded, valid id is kept,
 *   otherwise we fall back to "categories".
 * - `collection:<id>` → kept when the id is a known collection, else "collections".
 * - anything else → `fallback`.
 */
export function resolveActiveTab(viewParam, content, fallback) {
    if (!viewParam)
        return fallback;
    if (TOP_LEVEL_TABS.includes(viewParam))
        return viewParam;
    if (viewParam.startsWith("section:")) {
        if (!content)
            return viewParam;
        const id = viewParam.slice("section:".length);
        return content.sections.some((s) => s.id === id) ? viewParam : "categories";
    }
    if (viewParam.startsWith("collection:")) {
        const id = viewParam.slice("collection:".length);
        return COLLECTION_IDS.includes(id) ? viewParam : "collections";
    }
    return fallback;
}
// ── Immutable content helpers ──────────────────────────────────────────────
export function mapItem(content, sectionId, subId, itemId, updater) {
    return {
        sections: content.sections.map((s) => s.id !== sectionId
            ? s
            : {
                ...s,
                subsections: s.subsections.map((sub) => sub.id !== subId
                    ? sub
                    : {
                        ...sub,
                        items: sub.items.map((item) => item.id !== itemId ? item : updater(item)),
                    }),
            }),
    };
}
export function mapSubsection(content, sectionId, subId, updater) {
    return {
        sections: content.sections.map((s) => s.id !== sectionId
            ? s
            : {
                ...s,
                subsections: s.subsections.map((sub) => sub.id !== subId ? sub : updater(sub)),
            }),
    };
}
export function mapSection(content, sectionId, updater) {
    return {
        sections: content.sections.map((s) => (s.id !== sectionId ? s : updater(s))),
    };
}
export function addItem(content, sectionId, subId, item) {
    return mapSubsection(content, sectionId, subId, (sub) => ({
        ...sub,
        items: [...sub.items, item],
    }));
}
/**
 * Append several items to a subsection in a single immutable update so a bulk
 * add triggers exactly one content change / save. Ordering is preserved.
 */
export function addItems(content, sectionId, subId, items) {
    return mapSubsection(content, sectionId, subId, (sub) => ({
        ...sub,
        items: [...sub.items, ...items],
    }));
}
export function deleteItem(content, sectionId, subId, itemId) {
    return mapSubsection(content, sectionId, subId, (sub) => ({
        ...sub,
        items: sub.items.filter((i) => i.id !== itemId),
    }));
}
export function addSubsection(content, sectionId, sub) {
    return mapSection(content, sectionId, (s) => ({
        ...s,
        subsections: [...s.subsections, sub],
    }));
}
export function deleteSubsection(content, sectionId, subId) {
    return mapSection(content, sectionId, (s) => ({
        ...s,
        subsections: s.subsections.filter((sub) => sub.id !== subId),
    }));
}
export function addSection(content, section) {
    return { sections: [...content.sections, section] };
}
export function deleteSection(content, sectionId) {
    return { sections: content.sections.filter((s) => s.id !== sectionId) };
}
/**
 * Move a subsection (with its id and all items preserved) from one category to
 * another. Removes it from the source section and appends it to the bottom of
 * the destination section. The source category is left in place even if it
 * becomes empty. No-op when source/destination are the same or either is missing.
 */
export function moveSubsection(content, fromSectionId, toSectionId, subId) {
    if (fromSectionId === toSectionId)
        return content;
    const fromSection = content.sections.find((s) => s.id === fromSectionId);
    const sub = fromSection?.subsections.find((su) => su.id === subId);
    if (!sub)
        return content;
    return {
        sections: content.sections.map((s) => {
            if (s.id === fromSectionId) {
                return { ...s, subsections: s.subsections.filter((su) => su.id !== subId) };
            }
            if (s.id === toSectionId) {
                return { ...s, subsections: [...s.subsections, sub] };
            }
            return s;
        }),
    };
}
export function moveItemUp(content, sectionId, subId, itemId) {
    return mapSubsection(content, sectionId, subId, (sub) => {
        const idx = sub.items.findIndex((i) => i.id === itemId);
        if (idx <= 0)
            return sub;
        const items = [...sub.items];
        [items[idx - 1], items[idx]] = [items[idx], items[idx - 1]];
        return { ...sub, items };
    });
}
export function moveItemDown(content, sectionId, subId, itemId) {
    return mapSubsection(content, sectionId, subId, (sub) => {
        const idx = sub.items.findIndex((i) => i.id === itemId);
        if (idx < 0 || idx >= sub.items.length - 1)
            return sub;
        const items = [...sub.items];
        [items[idx], items[idx + 1]] = [items[idx + 1], items[idx]];
        return { ...sub, items };
    });
}
// ── Presence ─────────────────────────────────────────────────────────────────
/**
 * Collapse active-user rows into one entry per logical participant. Groups by
 * `participantId` when present, otherwise by normalized display name. Defensive:
 * even if the backend returns one row per device, the header/people views render
 * a single badge per person with a summed device count.
 */
export function dedupeActiveUsers(users) {
    const byKey = new Map();
    for (const u of users) {
        const key = u.participantId ?? u.displayName.trim().replace(/\s+/g, " ").toLowerCase();
        const existing = byKey.get(key);
        const devices = u.deviceCount ?? 1;
        if (!existing) {
            byKey.set(key, { ...u, deviceCount: devices });
            continue;
        }
        // Keep the most-recently-seen display name; sum device counts; max lastSeen.
        const latest = u.lastSeen >= existing.lastSeen;
        byKey.set(key, {
            participantId: existing.participantId ?? u.participantId,
            displayName: latest ? u.displayName : existing.displayName,
            lastSeen: Math.max(existing.lastSeen, u.lastSeen),
            deviceCount: (existing.deviceCount ?? 1) + devices,
        });
    }
    return [...byKey.values()];
}
// ── Avatar ─────────────────────────────────────────────────────────────────
const AVATAR_COLORS = [
    "bg-indigo-500",
    "bg-emerald-500",
    "bg-rose-500",
    "bg-amber-500",
    "bg-violet-500",
    "bg-cyan-500",
    "bg-teal-500",
    "bg-orange-500",
];
export function avatarColor(name) {
    let hash = 0;
    for (const ch of name)
        hash = (hash * 31 + ch.charCodeAt(0)) & 0xfffffff;
    return AVATAR_COLORS[hash % AVATAR_COLORS.length];
}
// Returns a map of name → color for a list of names, ensuring no two names
// share a color (as long as there are fewer names than available colors).
export function assignAvatarColors(names) {
    const used = new Set();
    const result = {};
    for (const name of names) {
        if (name in result)
            continue;
        let hash = 0;
        for (const ch of name)
            hash = (hash * 31 + ch.charCodeAt(0)) & 0xfffffff;
        let idx = hash % AVATAR_COLORS.length;
        const start = idx;
        while (used.has(AVATAR_COLORS[idx])) {
            idx = (idx + 1) % AVATAR_COLORS.length;
            if (idx === start)
                break;
        }
        result[name] = AVATAR_COLORS[idx];
        used.add(AVATAR_COLORS[idx]);
    }
    return result;
}
// Hebrew family-name prefix: "משפחת כהן" → "כ", "משפחה ישראלי" → "י"
const FAMILY_PREFIX = /^משפחת?\s+/;
export function avatarInitial(name) {
    const trimmed = name.trim();
    const withoutPrefix = FAMILY_PREFIX.test(trimmed)
        ? trimmed.replace(FAMILY_PREFIX, "")
        : trimmed;
    return withoutPrefix.charAt(0).toUpperCase() || "?";
}
// ── Time ────────────────────────────────────────────────────────────────────
export function relativeTime(ts) {
    const diff = Math.floor(Date.now() / 1000) - ts;
    if (diff < 5)
        return "זה עתה";
    if (diff < 60)
        return `לפני ${diff} שניות`;
    if (diff < 3600)
        return `לפני ${Math.floor(diff / 60)} דקות`;
    if (diff < 86400)
        return `לפני ${Math.floor(diff / 3600)} שעות`;
    return `לפני ${Math.floor(diff / 86400)} ימים`;
}
